/* ============================================================
   Urban Junglist Co. - Atmospheric particles

   A sparse, slow, low-opacity dust field on a single canvas. Standalone
   like the countdown: no GSAP dependency, and a failure here cannot
   reach the signup or the entrance.

   Fog is pure CSS and lives in assets/ujc-atmosphere.css. Nothing in
   this file touches it.

   ------------------------------------------------------------
   WINDOW FLICKER - INTERFACE ONLY, NOT IMPLEMENTED
   ------------------------------------------------------------
   Deferred until the separated buildings layer exists. Placing lights
   against the flattened artwork would mean guessing, and every guess
   would have to be redone once the real layer arrives.

   When the buildings layer is uploaded, activation needs one array and
   one renderer. The agreed interface:

     Coordinate space: percentages of the .ujc-scene box, which is a
     square matching the 2048 x 2048 layer canvas. Origin top-left.
     Values therefore transfer directly from the artwork file: a window
     whose centre sits at pixel (880, 742) becomes x: 42.97, y: 36.23.

     Shape: [{ x, y, w, h }, ...] with w and h also in percent.

     Constraints agreed for the implementation:
       - 8 to 16 entries, no more
       - never illuminate every window at once; a small random subset
       - gradual opacity fades, never instant switching
       - irregular intervals, no fixed cadence
       - restrained peak brightness
       - paused via the Page Visibility API and an IntersectionObserver,
         matching the particle field below

   No coordinates are committed here on purpose. Do not add placeholder
   values - an inaccurate light is worse than no light.
   ------------------------------------------------------------
   ============================================================ */

(function () {
  'use strict';

  var registry = new WeakMap();

  /* Rendering above 1.5x buys nothing for sub-2px dots and costs real
     fill rate on high-density phones. */
  var MAX_DPR = 1.5;
  var BASE_FINE = 34;
  var BASE_COARSE = 16;

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function isCoarsePointer() {
    return !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
  }

  /* Conservative low-power detection. Any positive signal disables the
     field outright rather than merely thinning it - decorative dust is
     never worth a dropped frame on a budget handset or a metered
     connection. */
  function isLowPower() {
    var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection && connection.saveData) return true;
    if (typeof navigator.deviceMemory === 'number' && navigator.deviceMemory <= 4) return true;
    if (typeof navigator.hardwareConcurrency === 'number' && navigator.hardwareConcurrency <= 4) return true;
    return false;
  }

  function Field(canvas) {
    this.canvas = canvas;
    this.context = canvas.getContext ? canvas.getContext('2d') : null;
    this.section = canvas.closest ? canvas.closest('.ujc-prelaunch') : null;

    if (!this.context || !this.section) return;

    var raw = parseFloat(canvas.getAttribute('data-intensity'));
    this.intensity = isNaN(raw) ? 0.5 : Math.max(0, Math.min(1, raw));

    this.particles = [];
    this.frame = null;
    this.running = false;
    this.inView = true;
    this.last = 0;
    this.width = 0;
    this.height = 0;

    this.resize();
    this.populate();
    this.observe();
    this.start();
  }

  Field.prototype.count = function () {
    var base = isCoarsePointer() ? BASE_COARSE : BASE_FINE;
    /* Intensity 0.5 is unity; the slider spans roughly half to one and a
       half times the base count. */
    var scaled = Math.round(base * (0.5 + this.intensity));
    var floor = isCoarsePointer() ? 10 : 18;
    var ceiling = isCoarsePointer() ? 26 : 54;
    return Math.max(floor, Math.min(ceiling, scaled));
  };

  Field.prototype.resize = function () {
    /* One getBoundingClientRect per resize, never inside a frame. */
    var rect = this.canvas.getBoundingClientRect();
    var dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);

    this.width = rect.width || 1;
    this.height = rect.height || 1;

    this.canvas.width = Math.round(this.width * dpr);
    this.canvas.height = Math.round(this.height * dpr);
    this.context.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  Field.prototype.spawn = function (seeded) {
    return {
      x: Math.random() * this.width,
      y: seeded ? Math.random() * this.height : this.height + Math.random() * 40,
      radius: 0.5 + Math.random() * 1.2,
      vx: (Math.random() - 0.5) * 0.055,
      vy: -(0.045 + Math.random() * 0.095),
      peak: 0.05 + Math.random() * 0.095,
      phase: Math.random() * Math.PI * 2,
      drift: 0.0035 + Math.random() * 0.0055,
      violet: Math.random() < 0.16
    };
  };

  Field.prototype.populate = function () {
    var target = this.count();
    this.particles.length = 0;
    for (var i = 0; i < target; i++) {
      this.particles.push(this.spawn(true));
    }
  };

  Field.prototype.render = function (now) {
    if (!this.running) return;

    /* Normalised against a 60Hz frame and clamped, so a 120Hz display
       does not double the drift speed and a stalled tab does not
       teleport everything on resume. */
    var delta = this.last ? Math.min((now - this.last) / 16.67, 3) : 1;
    this.last = now;

    var ctx = this.context;
    ctx.clearRect(0, 0, this.width, this.height);

    for (var i = 0; i < this.particles.length; i++) {
      var p = this.particles[i];

      p.x += p.vx * delta;
      p.y += p.vy * delta;
      p.phase += p.drift * delta;

      if (p.y < -6) {
        this.particles[i] = this.spawn(false);
        continue;
      }
      if (p.x < -6) p.x = this.width + 6;
      if (p.x > this.width + 6) p.x = -6;

      var alpha = p.peak * (0.5 + 0.5 * Math.sin(p.phase));
      if (alpha <= 0.004) continue;

      ctx.fillStyle = p.violet
        ? 'rgba(154, 82, 232, ' + alpha.toFixed(3) + ')'
        : 'rgba(226, 222, 236, ' + alpha.toFixed(3) + ')';

      ctx.beginPath();
      ctx.arc(p.x, p.y, p.radius, 0, 6.2832);
      ctx.fill();
    }

    var self = this;
    this.frame = window.requestAnimationFrame(function (t) { self.render(t); });
  };

  Field.prototype.start = function () {
    if (this.running) return;
    this.running = true;
    this.last = 0;

    var self = this;
    this.frame = window.requestAnimationFrame(function (t) { self.render(t); });
  };

  Field.prototype.stop = function () {
    this.running = false;
    if (this.frame) {
      window.cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  };

  Field.prototype.sync = function () {
    if (document.hidden || !this.inView) {
      this.stop();
      return;
    }
    this.start();
  };

  Field.prototype.observe = function () {
    var self = this;

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(function () {
        self.resize();
        self.populate();
      });
      this.resizeObserver.observe(this.canvas);
    }

    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(function (entries) {
        self.inView = entries[0].isIntersecting;
        self.sync();
      }, { threshold: 0 });
      this.intersectionObserver.observe(this.section);
    }

    this.onVisibility = function () { self.sync(); };
    document.addEventListener('visibilitychange', this.onVisibility);

    if (window.matchMedia) {
      var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onMotionChange = function () {
        if (!mq.matches) return;
        self.stop();
        self.context.clearRect(0, 0, self.width, self.height);
      };
      if (mq.addEventListener) {
        mq.addEventListener('change', this.onMotionChange);
        this.motionQuery = mq;
      } else if (mq.addListener) {
        mq.addListener(this.onMotionChange);
        this.motionQueryLegacy = mq;
      }
    }
  };

  Field.prototype.destroy = function () {
    this.stop();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    if (this.onVisibility) {
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.onVisibility = null;
    }
    if (this.motionQuery && this.onMotionChange) {
      this.motionQuery.removeEventListener('change', this.onMotionChange);
    }
    if (this.motionQueryLegacy && this.onMotionChange) {
      this.motionQueryLegacy.removeListener(this.onMotionChange);
    }
    this.particles.length = 0;
  };

  function mount(root) {
    if (prefersReducedMotion() || isLowPower()) return;

    var scope = root || document;
    var canvases = scope.querySelectorAll('.ujc-atmosphere__particles');

    Array.prototype.forEach.call(canvases, function (canvas) {
      if (registry.has(canvas)) return;
      registry.set(canvas, new Field(canvas));
    });
  }

  function unmount(canvas) {
    var field = registry.get(canvas);
    if (!field) return;
    if (field.destroy) field.destroy();
    registry.delete(canvas);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { mount(); });
  } else {
    mount();
  }

  document.addEventListener('shopify:section:load', function (event) {
    mount(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var stale = event.target.querySelectorAll('.ujc-atmosphere__particles');
    Array.prototype.forEach.call(stale, unmount);
  });
})();
